import { Content, ExpandableSection } from '@patternfly/react-core';
import * as React from 'react';

export interface AddressAsReceivedProps {
  /**
   * The address exactly as upstream sent it - `PrivateKlass.deliveryAddressText` or
   * `Instructor.homeAddressText`. Nothing is rendered when it is absent.
   */
  value: string | undefined | null;
  /** Distinguishes the toggle when a page could ever show two of these. */
  toggleText?: string;
}

/**
 * The address as upstream sent it: the evidence behind the structured address above it.
 *
 * **READ-ONLY, AND NOT A FIELD.** The service owns both text columns - they are
 * `@JsonProperty(access = READ_ONLY)` and the writes carry the stored value across a save - so there
 * is nothing here to bind a control to, and a save that omitted them could not blank them even if
 * one tried.
 *
 * **NOT A WARNING EITHER.** These columns are written on every import, whether the address could be
 * read or not, so their presence says only "this is what arrived". The test for an address that
 * could not be read is `isDeliveryAddressUnreadable` in model/Klass - the text plus an address with
 * no lines - and the place a reader is told is the automation message panel.
 *
 * COLLAPSED BY DEFAULT, for two reasons. It is evidence somebody goes looking for rather than
 * something to read every time a class is opened; and for instructors there is a migration window
 * in which this and `homeAddress.line1` hold the same blob - changelog 0040 backfills the text from
 * the address lines without clearing them, so that the readable copy on screen survives until that
 * instructor's next message. Stacked open, those two would look like a rendering fault.
 *
 * `pre-wrap` because many of these values are genuinely multi-line: 71 of the 167 instructor
 * addresses had raw newlines inside `line1`, and the newlines are part of what was received.
 */
export const AddressAsReceived: React.FunctionComponent<AddressAsReceivedProps> = ({
  value,
  toggleText = 'As received'
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!value?.trim()) return null;

  return (
    <ExpandableSection
      toggleText={toggleText}
      onToggle={(_e, expanded) => setIsExpanded(expanded)}
      isExpanded={isExpanded}
      isIndented
    >
      <Content component="p" style={{ whiteSpace: 'pre-wrap' }}>{value}</Content>
      <Content component="small">
        The address as it was received, kept whether or not it could be read into the address above.
        Not editable here - it is the record of what was sent.
      </Content>
    </ExpandableSection>
  );
};
